import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { t } from 'ttag';
import { Heading } from 'components';

import './CardSlider.css';

function CardSlider({ title, cards, cardSize }) {
  const cardSlider = useRef(null);

  const navigateSlider = useCallback(direction => {
    if (cardSlider.current) {
      if (direction === 'next') cardSlider.current.scrollLeft += 300;
      if (direction === 'previous') cardSlider.current.scrollLeft -= 300;
    }
  }, []);

  if (!cards.length) return null;

  return (
    <div className={clsx('mx-auto max-w-screen-xl')}>
      <div className={clsx('flex items-start justify-between gap-4')}>
        {title && (
          <div className={clsx('m-2 font-semibold')}>
            <Heading as="h2" noMargin={true}>
              {title}
            </Heading>
          </div>
        )}
        <div className={clsx('flex gap-4')}>
          <button
            className={clsx(
              'flex shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 hover:bg-whisper'
            )}
            type="button"
            onClick={() => navigateSlider('previous')}
            aria-label={t`Previous`}
          >
            <RiArrowLeftSLine className={clsx('h-6 w-6')} />
          </button>
          <button
            className={clsx(
              'flex shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 hover:bg-whisper'
            )}
            type="button"
            onClick={() => navigateSlider('next')}
            aria-label={t`Next`}
          >
            <RiArrowRightSLine className={clsx('h-6 w-6')} />
          </button>
        </div>
      </div>
      <ul
        className={clsx(
          'hide-scrollbar flex w-full snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth p-0 before:w-[30vw] before:shrink-0 after:w-[30vw] after:shrink-0'
        )}
        ref={cardSlider}
      >
        {cards.map(card => (
          <li
            className={clsx(
              {
                'w-40': cardSize === 'sm',
                'w-60': cardSize === 'md',
                'w-96': cardSize === 'lg'
              },
              'group relative m-2 shrink-0 cursor-pointer snap-center list-none ring-1 ring-whisper hover:bg-green'
            )}
            key={card.id}
          >
            <a className={clsx('no-underline')} href={card.link}>
              <div
                className={clsx(
                  'aspect-h-4 aspect-w-3 w-full bg-whisper bg-cover bg-center bg-no-repeat'
                )}
                style={{ backgroundImage: `url(${card.image})` }}
              />
              <div className={clsx('gap-4 p-4')}>
                <h3
                  className={clsx(
                    'm-0 text-base font-semibold text-night-rider group-hover:text-white'
                  )}
                >
                  {card.title}
                </h3>
                {card.description && (
                  <p
                    className={clsx(
                      'm-0 text-sm text-black group-hover:text-white'
                    )}
                  >
                    {card.description}
                  </p>
                )}
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

CardSlider.propTypes = {
  title: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string,
      link: PropTypes.string.isRequired
    })
  ),
  cardSize: PropTypes.oneOf(['sm', 'md', 'lg'])
};

CardSlider.defaultProps = {
  title: undefined,
  cards: [],
  cardSize: 'md'
};

export default CardSlider;
